import { useState } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Copy } from "lucide-react";
import { cn } from "@/lib/utils";

export interface DayHours {
  isOpen: boolean;
  openTime: string;
  closeTime: string;
}

export interface OpeningHours {
  monday: DayHours;
  tuesday: DayHours;
  wednesday: DayHours;
  thursday: DayHours;
  friday: DayHours;
  saturday: DayHours;
  sunday: DayHours;
}

const DAYS: (keyof OpeningHours)[] = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_LABELS: Record<keyof OpeningHours, string> = {
  monday: "Monday",
  tuesday: "Tuesday",
  wednesday: "Wednesday",
  thursday: "Thursday",
  friday: "Friday",
  saturday: "Saturday",
  sunday: "Sunday",
};

export const DEFAULT_OPENING_HOURS: OpeningHours = {
  monday: { isOpen: true, openTime: "09:00", closeTime: "17:00" },
  tuesday: { isOpen: true, openTime: "09:00", closeTime: "17:00" },
  wednesday: { isOpen: true, openTime: "09:00", closeTime: "17:00" },
  thursday: { isOpen: true, openTime: "09:00", closeTime: "17:00" },
  friday: { isOpen: true, openTime: "09:00", closeTime: "17:00" },
  saturday: { isOpen: false, openTime: "09:00", closeTime: "17:00" },
  sunday: { isOpen: false, openTime: "09:00", closeTime: "17:00" },
};

interface OpeningHoursEditorProps {
  value: OpeningHours;
  onChange: (hours: OpeningHours) => void;
}

export const OpeningHoursEditor = ({ value, onChange }: OpeningHoursEditorProps) => {
  const [copySourceDay, setCopySourceDay] = useState<keyof OpeningHours | null>(null);

  const updateDay = (day: keyof OpeningHours, updates: Partial<DayHours>) => {
    onChange({
      ...value,
      [day]: { ...value[day], ...updates },
    });
  };

  const copyTimesToOtherDays = (sourceDay: keyof OpeningHours) => {
    const sourceHours = value[sourceDay];
    const newHours = { ...value };
    
    DAYS.forEach((day) => {
      if (day !== sourceDay) {
        newHours[day] = { ...sourceHours };
      }
    });
    
    onChange(newHours);
    setCopySourceDay(null);
  };

  return (
    <div className="space-y-4">
      <Label>Opening Hours *</Label>
      <div className="space-y-3 rounded-lg border border-border p-4">
        {DAYS.map((day) => (
          <div
            key={day}
            className={cn(
              "flex flex-wrap items-center gap-3 py-2 border-b border-border last:border-b-0",
              !value[day].isOpen && "opacity-60"
            )}
          >
            <div className="flex items-center gap-2 w-32">
              <Checkbox
                id={`open-${day}`}
                checked={value[day].isOpen}
                onCheckedChange={(checked) =>
                  updateDay(day, { isOpen: checked === true })
                }
              />
              <Label
                htmlFor={`open-${day}`}
                className="text-sm font-medium cursor-pointer"
              >
                {DAY_LABELS[day]}
              </Label>
            </div>

            {value[day].isOpen ? (
              <>
                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={value[day].openTime}
                    onChange={(e) => updateDay(day, { openTime: e.target.value })}
                    className="w-32 border-border"
                  />
                  <span className="text-muted-foreground">to</span>
                  <Input
                    type="time"
                    value={value[day].closeTime}
                    onChange={(e) => updateDay(day, { closeTime: e.target.value })}
                    className="w-32 border-border"
                  />
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => copyTimesToOtherDays(day)}
                  className="text-xs text-muted-foreground hover:text-foreground"
                >
                  <Copy className="w-3 h-3 mr-1" />
                  Copy to all
                </Button>
              </>
            ) : (
              <span className="text-sm text-muted-foreground">Closed</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// Helper function to convert OpeningHours to legacy availability string
export const openingHoursToString = (hours: OpeningHours): string => {
  const openDays = DAYS.filter((day) => hours[day].isOpen);
  
  if (openDays.length === 0) return "Closed";
  if (openDays.length === 7) {
    const first = hours[openDays[0]];
    const allSame = openDays.every(
      (day) =>
        hours[day].openTime === first.openTime &&
        hours[day].closeTime === first.closeTime
    );
    if (allSame) {
      return `Daily ${formatTime(first.openTime)} - ${formatTime(first.closeTime)}`;
    }
  }
  
  // Group consecutive days with same hours
  return openDays
    .map((day) => `${DAY_LABELS[day].slice(0, 3)} ${formatTime(hours[day].openTime)}-${formatTime(hours[day].closeTime)}`)
    .join(", ");
};

const formatTime = (time: string): string => {
  const [hours, minutes] = time.split(":");
  const h = parseInt(hours, 10);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${minutes} ${ampm}`;
};

// Helper to check if currently open
export const isCurrentlyOpen = (hours: OpeningHours | null): boolean => {
  if (!hours) return false;
  
  const now = new Date();
  const dayIndex = now.getDay(); // 0 = Sunday
  const dayMap: (keyof OpeningHours)[] = [
    "sunday",
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
  ];
  const today = dayMap[dayIndex];
  const todayHours = hours[today];
  
  if (!todayHours?.isOpen) return false;
  
  const currentTime = `${now.getHours().toString().padStart(2, "0")}:${now.getMinutes().toString().padStart(2, "0")}`;
  return currentTime >= todayHours.openTime && currentTime <= todayHours.closeTime;
};
